import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Download, MessageSquarePlus, Trash2, Search, ChevronRight, UserPlus, Copy, CheckCircle2 } from "lucide-react";
import Nav from "../components/Nav";
import { api, formatApiErrorDetail } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const STATUSES = ["Submitted", "In Progress", "Resolved"];
const BUREAUS = ["Experian", "Equifax", "TransUnion"];
const TYPES = ["Late Payment", "Collection", "Charge-Off", "Inquiry", "Bankruptcy", "Judgment", "Other"];

function bytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmt(dt) {
    if (!dt) return "—";
    try {
        return new Date(dt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return "—";
    }
}

function statusVariant(s) {
    switch (s) {
        case "Resolved":
            return "default";
        case "In Progress":
            return "secondary";
        default:
            return "outline";
    }
}

/* -------------------------------------------------------------------------- */
/*  New Dispute Dialog                                                        */
/* -------------------------------------------------------------------------- */

function NewDisputeDialog({ userId, clientName, onCreated }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        bureau: "Experian",
        creditor: "",
        type: "Collection",
        amount: "",
        status: "Submitted",
        note: "",
    });

    const submit = async () => {
        if (!form.creditor.trim()) {
            toast.error("Creditor is required.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/admin/disputes", {
                user_id: userId,
                bureau: form.bureau,
                creditor: form.creditor.trim(),
                type: form.type,
                amount: parseFloat(form.amount || "0") || 0,
                status: form.status,
                note: form.note || null,
            });
            toast.success(`Dispute filed for ${clientName}.`);
            setOpen(false);
            setForm({ bureau: "Experian", creditor: "", type: "Collection", amount: "", status: "Submitted", note: "" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="admin-new-dispute-btn"
                    className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-accent"
                >
                    <Plus size={14} /> File dispute
                </button>
            </DialogTrigger>
            <DialogContent className="rounded-none border hairline p-8 max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-editorial text-3xl tracking-tighter">
                        File a dispute for <span className="italic text-accent">{clientName}</span>
                    </DialogTitle>
                    <DialogDescription className="font-mono-ui text-xs text-muted-foreground">
                        Add a new dispute to this client&rsquo;s file. Status auto-sets progress; an
                        initial note is optional.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <FieldSelect label="Bureau" value={form.bureau} onChange={(v) => setForm({ ...form, bureau: v })} options={BUREAUS} testId="new-dispute-bureau" />
                    <FieldSelect label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES} testId="new-dispute-type" />
                    <FieldInput label="Creditor" value={form.creditor} onChange={(v) => setForm({ ...form, creditor: v })} testId="new-dispute-creditor" required />
                    <FieldInput label="Amount ($)" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} type="number" testId="new-dispute-amount" />
                    <FieldSelect label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testId="new-dispute-status" />
                </div>
                <label className="block mt-4">
                    <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                        Initial progress note (optional)
                    </span>
                    <textarea
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        rows={3}
                        data-testid="new-dispute-note"
                        className="w-full bg-transparent border hairline focus:border-foreground outline-none px-3 py-2 font-mono-ui text-sm"
                    />
                </label>
                <DialogFooter>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="new-dispute-submit"
                        className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-accent disabled:opacity-60"
                    >
                        {busy ? "Filing…" : "File dispute"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldInput({ label, value, onChange, type = "text", required, testId }) {
    return (
        <label className="block">
            <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                {label}
                {required && <span className="text-accent"> *</span>}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-transparent border hairline focus:border-foreground outline-none px-3 py-2 font-mono-ui text-sm"
            />
        </label>
    );
}

function FieldSelect({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                {label}
            </span>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testId} className="rounded-none border hairline font-mono-ui text-sm">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border hairline">
                    {options.map((o) => (
                        <SelectItem key={o} value={o} className="rounded-none font-mono-ui text-sm">
                            {o}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </label>
    );
}

/* -------------------------------------------------------------------------- */
/*  Dispute row w/ status editor + notes                                      */
/* -------------------------------------------------------------------------- */

function DisputeRow({ dispute, onChanged }) {
    const [open, setOpen] = useState(false);
    const [noteBody, setNoteBody] = useState("");
    const [busy, setBusy] = useState(false);

    const changeStatus = async (status) => {
        try {
            await api.patch(`/admin/disputes/${dispute.id}`, { status });
            toast.success(`Status → ${status}`);
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const addNote = async () => {
        if (!noteBody.trim()) return;
        setBusy(true);
        try {
            await api.post(`/admin/disputes/${dispute.id}/notes`, { body: noteBody.trim() });
            setNoteBody("");
            toast.success("Note added.");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete dispute against ${dispute.creditor}?`)) return;
        try {
            await api.delete(`/admin/disputes/${dispute.id}`);
            toast.success("Dispute deleted.");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <>
            <TableRow data-testid={`admin-dispute-row-${dispute.id}`} className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
                <TableCell>
                    <ChevronRight
                        size={14}
                        className={`transition-transform ${open ? "rotate-90" : ""}`}
                    />
                </TableCell>
                <TableCell className="font-editorial text-lg">{dispute.creditor}</TableCell>
                <TableCell className="font-mono-ui text-xs">{dispute.bureau}</TableCell>
                <TableCell className="text-sm">{dispute.type}</TableCell>
                <TableCell className="font-mono-ui text-xs">
                    {dispute.amount ? `$${Number(dispute.amount).toLocaleString()}` : "—"}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={dispute.status} onValueChange={changeStatus}>
                        <SelectTrigger
                            data-testid={`admin-status-select-${dispute.id}`}
                            className="rounded-none border hairline font-mono-ui text-xs h-9 w-[140px]"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none border hairline">
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="rounded-none font-mono-ui text-xs">
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TableCell>
                <TableCell className="min-w-[140px]">
                    <Progress value={dispute.progress || 0} className="h-1 rounded-none" />
                </TableCell>
                <TableCell className="font-mono-ui text-[10px] text-muted-foreground">
                    {(dispute.notes || []).length}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                    <button
                        onClick={remove}
                        data-testid={`admin-dispute-delete-${dispute.id}`}
                        className="border hairline p-2 hover:bg-destructive hover:text-destructive-foreground"
                        aria-label="Delete"
                    >
                        <Trash2 size={12} />
                    </button>
                </TableCell>
            </TableRow>
            {open && (
                <TableRow>
                    <TableCell colSpan={9} className="bg-secondary/40" data-testid={`admin-dispute-detail-${dispute.id}`}>
                        <div className="grid grid-cols-12 gap-6 py-4">
                            <div className="col-span-12 md:col-span-4 text-xs font-mono-ui text-muted-foreground space-y-1">
                                <div>OPENED: {fmt(dispute.opened_at)}</div>
                                <div>UPDATED: {fmt(dispute.updated_at)}</div>
                                <div>PROGRESS: {dispute.progress}%</div>
                            </div>
                            <div className="col-span-12 md:col-span-8">
                                <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3 flex items-center gap-2">
                                    <MessageSquarePlus size={12} /> Progress notes
                                </div>
                                <ul className="space-y-3 mb-4">
                                    {(dispute.notes || []).length === 0 && (
                                        <li className="text-xs font-mono-ui text-muted-foreground">
                                            No notes yet.
                                        </li>
                                    )}
                                    {(dispute.notes || []).map((n) => (
                                        <li key={n.id} className="border-l-2 border-accent pl-3">
                                            <div className="text-sm">{n.body}</div>
                                            <div className="mt-1 text-[10px] tracking-[0.14em] uppercase text-muted-foreground">
                                                {n.author} · {fmt(n.created_at)}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="flex gap-2">
                                    <input
                                        value={noteBody}
                                        onChange={(e) => setNoteBody(e.target.value)}
                                        placeholder="Add a progress note…"
                                        data-testid={`admin-note-input-${dispute.id}`}
                                        className="flex-1 bg-background border hairline px-3 py-2 font-mono-ui text-sm outline-none focus:border-foreground"
                                    />
                                    <button
                                        onClick={addNote}
                                        disabled={busy}
                                        data-testid={`admin-note-submit-${dispute.id}`}
                                        className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 text-[10px] tracking-[0.16em] uppercase hover:bg-accent disabled:opacity-60"
                                    >
                                        Add note
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

/* -------------------------------------------------------------------------- */
/*  Client detail (disputes + documents)                                      */
/* -------------------------------------------------------------------------- */

function ClientDetail({ client, onChanged }) {
    const [disputes, setDisputes] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const backend = process.env.REACT_APP_BACKEND_URL;

    const load = async () => {
        setLoading(true);
        try {
            const [d, f] = await Promise.all([
                api.get(`/admin/disputes?user_id=${client.id}`),
                api.get(`/admin/documents?user_id=${client.id}`),
            ]);
            setDisputes(d.data);
            setDocs(f.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    const refresh = async () => {
        await load();
        onChanged?.();
    };

    return (
        <div className="border hairline bg-card p-6 md:p-8" data-testid={`admin-client-detail-${client.id}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                        Client file
                    </div>
                    <div className="font-editorial text-3xl md:text-4xl tracking-tighter">
                        {client.name}
                    </div>
                    <div className="mt-1 font-mono-ui text-xs text-muted-foreground">{client.email}</div>
                </div>
                <NewDisputeDialog userId={client.id} clientName={client.name} onCreated={refresh} />
            </div>

            <Tabs defaultValue="disputes" className="w-full">
                <TabsList className="rounded-none border hairline bg-transparent p-0 h-auto">
                    <TabsTrigger
                        value="disputes"
                        className="rounded-none px-4 py-2 text-[11px] tracking-[0.16em] uppercase data-[state=active]:bg-foreground data-[state=active]:text-background"
                        data-testid="admin-tab-disputes"
                    >
                        Disputes · {disputes.length}
                    </TabsTrigger>
                    <TabsTrigger
                        value="documents"
                        className="rounded-none px-4 py-2 text-[11px] tracking-[0.16em] uppercase data-[state=active]:bg-foreground data-[state=active]:text-background"
                        data-testid="admin-tab-documents"
                    >
                        Documents · {docs.length}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="disputes" className="mt-6">
                    {loading ? (
                        <div className="text-sm font-mono-ui text-muted-foreground py-8">Loading…</div>
                    ) : disputes.length === 0 ? (
                        <div className="border hairline py-10 text-center">
                            <div className="font-editorial text-xl">No disputes filed yet.</div>
                            <div className="mt-1 text-xs font-mono-ui text-muted-foreground">
                                Click “File dispute” to open the first one.
                            </div>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead></TableHead>
                                    <TableHead>Creditor</TableHead>
                                    <TableHead>Bureau</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Progress</TableHead>
                                    <TableHead>Notes</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {disputes.map((d) => (
                                    <DisputeRow key={d.id} dispute={d} onChanged={refresh} />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TabsContent>

                <TabsContent value="documents" className="mt-6">
                    {docs.length === 0 ? (
                        <div className="border hairline py-10 text-center">
                            <div className="font-editorial text-xl">No documents uploaded.</div>
                            <div className="mt-1 text-xs font-mono-ui text-muted-foreground">
                                Ask {client.name.split(" ")[0]} to upload their credit reports from the portal.
                            </div>
                        </div>
                    ) : (
                        <ul className="divide-y hairline border-t border-b hairline">
                            {docs.map((d) => (
                                <li
                                    key={d.id}
                                    data-testid={`admin-doc-row-${d.id}`}
                                    className="grid grid-cols-12 gap-4 items-center py-4"
                                >
                                    <div className="col-span-6 md:col-span-5">
                                        <div className="font-editorial text-lg leading-tight truncate">
                                            {d.original_name}
                                        </div>
                                        {d.note && <div className="text-xs text-muted-foreground truncate">{d.note}</div>}
                                    </div>
                                    <div className="col-span-3 md:col-span-2 font-mono-ui text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                                        {d.category}
                                    </div>
                                    <div className="hidden md:block col-span-2 font-mono-ui text-xs text-muted-foreground">
                                        {bytes(d.size)}
                                    </div>
                                    <div className="hidden md:block col-span-1 font-mono-ui text-xs text-muted-foreground">
                                        {fmt(d.uploaded_at)}
                                    </div>
                                    <div className="col-span-3 md:col-span-2 flex justify-end">
                                        <a
                                            href={`${backend}/api/admin/documents/${d.id}/download`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`admin-doc-download-${d.id}`}
                                            className="inline-flex items-center gap-1 border hairline px-3 py-2 text-[10px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                        >
                                            <Download size={12} /> Download
                                        </a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Enrollments panel                                                         */
/* -------------------------------------------------------------------------- */

function EnrollmentsPanel({ onPromoted }) {
    const [rows, setRows] = useState(null);
    const [creds, setCreds] = useState({}); // enrollment_id -> { email, temp_password }

    const load = async () => {
        try {
            const { data } = await api.get("/admin/enrollments");
            setRows(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const promote = async (row) => {
        try {
            const { data } = await api.post(`/admin/enrollments/${row.id}/promote`, {
                mark_paid: true,
            });
            if (data.existing_user) {
                toast.success(`${row.email} was already registered — marked as enrolled.`);
            } else {
                toast.success(`Account created for ${row.email}.`);
                setCreds((c) => ({ ...c, [row.id]: { email: data.email, temp_password: data.temp_password } }));
            }
            await load();
            onPromoted?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete enrollment for ${row.email}?`)) return;
        try {
            await api.delete(`/admin/enrollments/${row.id}`);
            toast.success("Enrollment removed.");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied.");
    };

    if (rows === null) {
        return (
            <div className="border hairline bg-card p-6 md:p-8">
                <div className="text-sm font-mono-ui text-muted-foreground">Loading enrollments…</div>
            </div>
        );
    }

    return (
        <div className="border hairline bg-card p-6 md:p-8" data-testid="admin-enrollments">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                        Payments · Enrollments
                    </div>
                    <div className="font-editorial text-3xl md:text-4xl tracking-tighter">
                        New sign-ups
                    </div>
                </div>
                <div className="font-mono-ui text-xs text-muted-foreground">
                    {rows.filter((r) => r.status === "pending").length} pending ·{" "}
                    {rows.filter((r) => r.status === "paid").length} paid
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="border hairline py-10 text-center">
                    <div className="font-editorial text-xl">No enrollments yet.</div>
                    <div className="mt-1 text-xs font-mono-ui text-muted-foreground">
                        Sign-ups from /enroll appear here.
                    </div>
                </div>
            ) : (
                <ul className="divide-y hairline border-t border-b hairline">
                    {rows.map((r) => {
                        const isPaid = r.status === "paid" || r.status === "converted";
                        const cred = creds[r.id];
                        return (
                            <li key={r.id} data-testid={`enrollment-row-${r.id}`} className="py-4">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <div className="col-span-12 md:col-span-4">
                                        <div className="font-editorial text-lg leading-tight">
                                            {r.name}
                                        </div>
                                        <div className="text-xs font-mono-ui text-muted-foreground truncate">
                                            {r.email}
                                        </div>
                                        {r.phone && (
                                            <div className="text-xs font-mono-ui text-muted-foreground">
                                                {r.phone}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-6 md:col-span-2 font-mono-ui text-xs text-muted-foreground">
                                        {r.goal || "—"}
                                    </div>
                                    <div className="col-span-6 md:col-span-2 font-mono-ui text-xs text-muted-foreground">
                                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                                    </div>
                                    <div className="col-span-6 md:col-span-2">
                                        <Badge
                                            variant={isPaid ? "default" : "outline"}
                                            className="rounded-none uppercase tracking-[0.14em] text-[10px]"
                                        >
                                            {r.status}
                                        </Badge>
                                    </div>
                                    <div className="col-span-6 md:col-span-2 flex items-center gap-2 justify-end">
                                        {!isPaid && (
                                            <button
                                                onClick={() => promote(r)}
                                                data-testid={`enrollment-promote-${r.id}`}
                                                className="inline-flex items-center gap-1 border hairline px-3 py-2 text-[10px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                                title="Mark as paid + create client account"
                                            >
                                                <UserPlus size={12} /> Create account
                                            </button>
                                        )}
                                        <button
                                            onClick={() => remove(r)}
                                            data-testid={`enrollment-delete-${r.id}`}
                                            className="border hairline p-2 hover:bg-destructive hover:text-destructive-foreground"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>

                                {/* One-shot credential preview after promotion */}
                                {cred && (
                                    <div className="mt-3 ml-0 md:ml-4 bg-accent text-accent-foreground p-4 grid grid-cols-12 gap-3 items-center">
                                        <div className="col-span-12 md:col-span-1">
                                            <CheckCircle2 size={20} />
                                        </div>
                                        <div className="col-span-12 md:col-span-8">
                                            <div className="text-[10px] tracking-[0.22em] uppercase text-accent-foreground/80">
                                                Share this with your client — shown once
                                            </div>
                                            <div className="mt-1 font-mono-ui text-sm break-all">
                                                <strong>Email:</strong> {cred.email}
                                            </div>
                                            <div className="font-mono-ui text-sm">
                                                <strong>Temp password:</strong>{" "}
                                                <span className="tracking-widest">{cred.temp_password}</span>
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-3 md:text-right">
                                            <button
                                                onClick={() => copy(`Email: ${cred.email}\nPassword: ${cred.temp_password}\nLogin: ${window.location.origin}/login`)}
                                                className="inline-flex items-center gap-2 bg-background text-foreground px-3 py-2 text-[10px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                            >
                                                <Copy size={12} /> Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Admin page                                                                */
/* -------------------------------------------------------------------------- */

export default function Admin() {
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/clients");
            setClients(data);
            if (!selected && data.length) setSelected(data[0]);
            if (selected) {
                const still = data.find((c) => c.id === selected.id);
                if (still) setSelected(still);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return clients;
        return clients.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
    }, [clients, query]);

    const totals = useMemo(() => {
        return clients.reduce(
            (a, c) => ({
                clients: a.clients + 1,
                docs: a.docs + (c.document_count || 0),
                active: a.active + (c.active_disputes || 0),
                disputes: a.disputes + (c.dispute_count || 0),
            }),
            { clients: 0, docs: 0, active: 0, disputes: 0 },
        );
    }, [clients]);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-28 md:pt-32 pb-24" data-testid="admin-root">
                <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-4 flex items-center gap-3">
                            <span className="w-8 h-px bg-muted-foreground" />
                            Admin console · Access Credit Repair
                        </div>
                        <h1 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                            Every client,
                            <br />
                            <span className="italic text-accent">every file.</span>
                        </h1>
                    </div>
                </div>

                {/* Totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { l: "Clients", v: totals.clients, t: "admin-total-clients" },
                        { l: "Total disputes", v: totals.disputes, t: "admin-total-disputes" },
                        { l: "Active items", v: totals.active, t: "admin-total-active" },
                        { l: "Documents", v: totals.docs, t: "admin-total-docs" },
                    ].map((s) => (
                        <motion.div
                            key={s.l}
                            data-testid={s.t}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="border hairline p-6 bg-card"
                        >
                            <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">{s.l}</div>
                            <div className="mt-3 font-editorial text-5xl tracking-tighter leading-none">{s.v}</div>
                        </motion.div>
                    ))}
                </div>

                <div className="grid grid-cols-12 gap-4">
                    {/* Client list */}
                    <aside className="col-span-12 lg:col-span-4 border hairline bg-card" data-testid="admin-client-list">
                        <div className="p-5 border-b hairline">
                            <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground mb-3">
                                Clients
                            </div>
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name or email"
                                    data-testid="admin-search"
                                    className="w-full bg-transparent border hairline px-9 py-2 font-mono-ui text-sm outline-none focus:border-foreground"
                                />
                            </div>
                        </div>
                        <ul className="max-h-[720px] overflow-y-auto">
                            {loading && (
                                <li className="p-5 font-mono-ui text-xs text-muted-foreground">Loading…</li>
                            )}
                            {!loading && filtered.length === 0 && (
                                <li className="p-5 font-mono-ui text-xs text-muted-foreground">No clients found.</li>
                            )}
                            {filtered.map((c) => {
                                const active = selected?.id === c.id;
                                return (
                                    <li key={c.id}>
                                        <button
                                            onClick={() => setSelected(c)}
                                            data-testid={`admin-client-${c.id}`}
                                            className={`w-full text-left px-5 py-4 border-b hairline transition-colors ${
                                                active ? "bg-foreground text-background" : "hover:bg-secondary"
                                            }`}
                                        >
                                            <div className="flex items-baseline justify-between gap-2">
                                                <div className="font-editorial text-xl truncate">{c.name || "(no name)"}</div>
                                                <div className={`font-mono-ui text-[10px] tracking-[0.16em] uppercase ${active ? "text-background/70" : "text-muted-foreground"}`}>
                                                    {c.active_disputes}/{c.dispute_count}
                                                </div>
                                            </div>
                                            <div className={`text-xs mt-1 truncate ${active ? "text-background/70" : "text-muted-foreground"}`}>
                                                {c.email}
                                            </div>
                                            <div className="mt-3 flex items-center gap-3">
                                                <Badge variant={statusVariant(c.active_disputes ? "In Progress" : "Resolved")} className="rounded-none uppercase tracking-[0.14em] text-[9px]">
                                                    {c.active_disputes ? "Active" : "Idle"}
                                                </Badge>
                                                <div className={`font-mono-ui text-[10px] ${active ? "text-background/70" : "text-muted-foreground"}`}>
                                                    {c.document_count} docs
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </aside>

                    <section className="col-span-12 lg:col-span-8">
                        {selected ? (
                            <ClientDetail client={selected} onChanged={load} />
                        ) : (
                            <div className="border hairline p-16 text-center bg-card">
                                <div className="font-editorial text-3xl">Select a client to begin.</div>
                            </div>
                        )}
                    </section>
                </div>

                {/* Enrollments section spans full width beneath */}
                <div className="mt-8">
                    <EnrollmentsPanel onPromoted={load} />
                </div>
            </div>
        </div>
    );
}
